import { useQuery } from "@tanstack/react-query";
import StudentCallView from "../calls/StudentCallView";
import SetupPreview from "../calls/SetupPreview";
import { scholarshipsApi } from "../../api/scholarships";
import { TYPE_LABELS, destinationOf, formatDuration, formatKigaliDate, fullName, kindOf, needsSponsor, placeOf } from "./model";
import { formatDateTime } from "./review/model";
import { evaluatorsKey } from "./EvaluatorsTab";
import { invitationsKey } from "./InvitationsTab";

const VISIBILITY = { PUBLIC: "Public", PRIVATE: "Private (invited students only)" };

// A programme's rules are its level's
const rulesOf = (scholarship, programme) => (scholarship.levels ?? []).find((l) => l.level.id === programme.level?.id)?.eligibility ?? programme.eligibility ?? [];

const evidenceOf = (file) => (file ? `${file.fileName}, uploaded ${formatDateTime(file.uploadedAt)}` : "Not uploaded yet");

/**
 * A scholarship before it is published: its whole set-up (details, programmes with who may apply,
 * documents and criteria, the review committee and, when private, the invited students), and the
 * page students will see, applying off.
 */
export default function ScholarshipPreview({ scholarship, onClose }) {
  const { one } = kindOf(scholarship.type);
  const isPrivate = scholarship.visibility === "PRIVATE";
  const { data: evaluators = [], isPending: evaluatorsPending } = useQuery({
    queryKey: evaluatorsKey(scholarship.id),
    queryFn: async () => (await scholarshipsApi.get(`admin/scholarships/${scholarship.id}/evaluators`)).data,
  });
  const { data: invitations = [], isPending: invitationsPending } = useQuery({
    queryKey: invitationsKey(scholarship.id),
    enabled: isPrivate,
    queryFn: async () => (await scholarshipsApi.get(`admin/scholarships/${scholarship.id}/invitations`)).data,
  });

  const programmes = scholarship.programmes ?? [];
  const details = [
    ["Type", TYPE_LABELS[scholarship.type] ?? scholarship.type],
    ...(needsSponsor(scholarship.type) ? [["Partner", scholarship.sponsor?.name]] : []),
    ["Destination", destinationOf(scholarship)],
    ["Academic year", scholarship.academicYear?.name],
    ["Opens", scholarship.opensAt && formatKigaliDate(scholarship.opensAt)],
    ["Closes", scholarship.closesAt && `${formatKigaliDate(scholarship.closesAt)}, 23:59 Kigali time`],
    ["Number of awards", scholarship.awardQuota == null ? "No limit set" : String(scholarship.awardQuota)],
    ["Visibility", VISIBILITY[scholarship.visibility] ?? scholarship.visibility],
    ["Approval evidence", evidenceOf(scholarship.approvalEvidence)],
    ...(isPrivate ? [["Why it is private", evidenceOf(scholarship.privacyEvidence)]] : []),
    ["Public summary", scholarship.publicSummary, true],
    ["Description", scholarship.description, true],
  ];

  return (
    <SetupPreview
      title={scholarship.name}
      details={details}
      programmes={programmes.map((p) => ({
        id: p.id,
        name: p.name,
        meta: [placeOf(p), p.level?.name, formatDuration(p.durationMonths)].filter(Boolean).join(" · "),
        rules: rulesOf(scholarship, p).map((r) => r.label),
        requirements: p.requirements ?? [],
        criteria: p.rubric?.length ? p.rubric : scholarship.sharedRubric ?? [],
      }))}
      people={[
        {
          title: "Scholarship Review Committee",
          people: evaluators.map((e) => ({ key: e.principalId, label: fullName(e) })),
          pending: evaluatorsPending,
          empty: "No evaluators yet.",
        },
        ...(isPrivate
          ? [{ title: "Invited students", people: invitations.map((i) => ({ key: i.id, label: i.email })), pending: invitationsPending, empty: "Nobody invited yet." }]
          : []),
      ]}
      studentPage={
        <StudentCallView
          preview
          title={scholarship.name}
          subtitle={`${destinationOf(scholarship)} · ${scholarship.sponsor?.name ?? "HEC"} · ${TYPE_LABELS[scholarship.type] ?? scholarship.type}`}
          aboutTitle={`About this ${one}`}
          description={scholarship.description || "No description yet."}
          programmes={programmes.map((p) => {
            const rules = rulesOf(scholarship, p);
            return {
              id: p.id,
              name: p.name,
              meta: [placeOf(p), p.level?.name, formatDuration(p.durationMonths)].filter(Boolean).join(" · "),
              requirements: p.requirements ?? [],
              whoMayApply: (
                <p className="text-sm text-slate-700">
                  <span className="font-medium">Who may apply:</span> {rules.length ? rules.map((r) => r.label).join("; ") : "any student"}
                </p>
              ),
            };
          })}
          closesAt={scholarship.closesAt}
          open
          canStart={false}
        />
      }
      onClose={onClose}
    />
  );
}
